/**
 * Global helper methods extracted from the legacy Vue 2 Global mixin.
 *
 * This module exposes Vue 3-friendly API (`useGlobal`) while
 * allowing `mixins/Global.ts` to remain a thin compatibility wrapper for Options API code.
 */

import { getCurrentInstance, isRef, ref, type Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { get, isEmpty } from 'lodash-es';
import { formatPhone, toMoney, getFullName } from '@/utils/index.js';
import { maskEmail, maskPhone } from '@/utils/mask.js';
import { timeHms } from '@/utils/time.js';
import { toCurrency } from '@/utils/numbers.js';
import i18n from '@/plugins/vue-i18n.js';
import { parseNewQuery } from '@/utils/navigation.ts';
import LeadType from '@/entities/LeadType.js';
import Moment from '@/value-objects/Moment.js';

export interface ResetOptions {
    redirectTo?: string;
}

type IconsMap = Record<string, string>;

export interface RouteLike {
    query?: Record<string, any>;
    meta?: Record<string, any>;
}

export interface RouterLike {
    push?: (to: any) => unknown;
    replace?: (to: any) => unknown;
}

interface GlobalDependencies {
    route?: RouteLike;
    router?: RouterLike;
    translate?: (key: string) => string;
    getIcons?: () => IconsMap;
    resetData?: () => void;
}

export interface UseGlobalOptions {
    route?: RouteLike;
    router?: RouterLike;
    translate?: (key: string) => string;
    icons?: IconsMap | Ref<IconsMap>;
    resetData?: () => void;
}

export interface GlobalMethods {
    $reset(options?: ResetOptions): void;
    average(numerator: number, denominator: number, stringify?: boolean): number | string;
    averageComma(
        numerator: number,
        denominator: number,
        stringify?: boolean,
        decimal?: number
    ): number | string;
    count(value: any): number;
    underscore(value: string): string;
    maskEmail(email: string): string;
    maskPhone(number: string): string;
    moment(date: any): string;
    futurMoment(date: any): string;
    filename(call: any, lead?: any): string;
    getExtension(communication: any): string;
    decodeString(html: string): string;
    navigateToLead(id: string | number): void;
    getFullName(obj: any, unknown?: boolean): string;
    getShortName(userOrAccount: any): string;
    timeHms(timeInSeconds: number, hideSeconds?: boolean): string;
    emailIsValid(email: string): boolean;
    validatePhone(phone: string): boolean;
    urlIsValid(url: string): boolean;
    toCurrency(
        value: number,
        decimals?: boolean,
        hidden?: boolean,
        fallback?: string,
        country?: string,
        lang?: string
    ): string;
    toMoney(decimal: number, fixed?: number, locale?: string, hidden?: boolean): string;
    formatPhone(number: string, withCountryCode?: boolean): string;
    getLeadType(id: string | number): string;
    getLeadTypeIcon(lead: any): string;
    formatExtension(text: string): string;
    as_locale(
        date: any,
        key: string,
        format?: string,
        timezone?: string,
        defaultNow?: boolean
    ): any;
    locale_dt(date: any, format?: string, timezone?: string, defaultNow?: boolean): any;
    localeDate(date: any, format?: string, timezone?: string, defaultNow?: boolean): any;
    locale_time(time: any, timezone: string): any;
    empty(value: any): boolean;
    updateQuery(newQueryData: Record<string, any>): void;
    getFileName(fileString: string): string;
    includeInLayout(item: string): boolean;
}

const resolveLocale = (): string => {
    const locale = (i18n as any).locale;

    if (typeof locale === 'string') {
        return locale;
    }

    if (typeof locale?.value === 'string') {
        return locale.value;
    }

    return 'en';
};

const defaultTranslate = (key: string): string => {
    const translated = (i18n as any).t?.(key);

    if (typeof translated === 'string') {
        return translated;
    }

    return String(translated ?? key);
};

/**
 * This function dynamically creates the methods based on the injected arguments.
 * It is the helper used to create the `useGlobal` composable.
 */
export const createGlobalMethods = ({
    route,
    router,
    translate = defaultTranslate,
    getIcons = () => ({}),
    resetData,
}: GlobalDependencies = {}): GlobalMethods => {
    const getLeadTypeById = (id: string | number): string => {
        return String(LeadType.getKey(id) ?? '');
    };

    return {
        // Resets component to its default state.
        $reset({ redirectTo }: ResetOptions = {}): void {
            resetData?.();

            if (redirectTo) {
                router?.push?.(redirectTo);
            }
        },

        average(numerator: number, denominator: number, stringify?: boolean): number | string {
            if (stringify) {
                return denominator ? Math.round(numerator / denominator) : 'N/A';
            }

            return denominator ? Math.round(numerator / denominator) : 0;
        },

        averageComma(
            numerator: number,
            denominator: number,
            stringify?: boolean,
            decimal: number = 1
        ): number | string {
            if (stringify) {
                return denominator
                    ? Math.round((numerator / denominator) * decimal) / decimal
                    : 'N/A';
            }

            return denominator ? Math.round((numerator / denominator) * decimal) / decimal : 0;
        },

        count(value: any): number {
            if (value !== undefined) {
                return typeof value === 'object' ? Object.keys(value).length : value.length;
            }

            return 0;
        },

        underscore(value: string): string {
            if (!value) {
                return '';
            }

            return value.replace(/([A-Z])/g, (match: string) => {
                return `_${match.toLowerCase()}`;
            });
        },

        maskEmail(email: string): string {
            return maskEmail(email);
        },

        maskPhone(number: string): string {
            return maskPhone(number);
        },

        moment(date: any): string {
            if (get(date, 'date')) {
                date = date.date;
            }

            if (Moment.now().diffInSeconds(date) < 60) {
                return translate('general.lessThanAMin');
            }

            return Moment.init(date).fromNow();
        },

        futurMoment(date: any): string {
            if (get(date, 'date')) {
                date = date.date;
            }

            return Moment.init(date).fromNow();
        },

        filename(call: any, lead: any = null): string {
            let fileName = '';

            const createdAt = Moment.asLocale(call.created_at, 'created_at');

            if (call.lead) {
                fileName += `${getFullName(call.lead)} ${createdAt.humanShort(true)}`;
            } else if (lead) {
                fileName += `${getFullName(lead)} ${createdAt.humanShort(true)}`;
            } else {
                fileName += createdAt.humanShort(true);
            }

            fileName = fileName.replace(/\s+/g, '-').toLowerCase();

            return fileName;
        },

        getExtension(communication: any): string {
            if (!communication || !communication.url) {
                return '';
            }

            if (communication.url.endsWith('.mp3')) {
                return '.mp3';
            }

            return '.wav';
        },

        decodeString(html: string): string {
            const txt = document.createElement('textarea');

            txt.innerHTML = html;

            return txt.value;
        },

        navigateToLead(id: string | number): void {
            if (!id) {
                return;
            }

            router?.push?.({
                name: 'leads.update',
                params: { id: String(id) },
            });
        },

        getFullName(obj: any, unknown: boolean = true): string {
            return getFullName(obj, unknown) as string;
        },

        getShortName(userOrAccount: any): string {
            let name = getFullName(userOrAccount) as string;

            if (name.indexOf(' ') > 0) {
                name = `${name.charAt(0)}. ${name.substring(name.indexOf(' '), name.length)}`;
            }

            return name;
        },

        timeHms(timeInSeconds: number, hideSeconds?: boolean): string {
            return timeHms(timeInSeconds, hideSeconds);
        },

        emailIsValid(email: string): boolean {
            const regex =
                // eslint-disable-next-line no-useless-escape
                /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

            return regex.test(email);
        },

        validatePhone(phone: string): boolean {
            const re =
                // eslint-disable-next-line
                /^(1\s|1|)?((\(\d{3}\))|\d{3})(\-|\s)?(\d{3})(\-|\s)?(\d{4})$/;
            return re.test(phone);
        },

        urlIsValid(url: string): boolean {
            const regex =
                // eslint-disable-next-line no-useless-escape
                /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

            return regex.test(url);
        },

        toCurrency(
            value: number,
            decimals: boolean = true,
            hidden: boolean = false,
            fallback: string = '',
            country: string = 'CA',
            lang: string = resolveLocale()
        ): string {
            return toCurrency(value, decimals, hidden, fallback, country, lang);
        },

        toMoney(
            decimal: number,
            fixed: number = 0,
            locale: string | undefined = undefined,
            hidden: boolean = false
        ): string {
            return toMoney(decimal, fixed, locale, hidden);
        },

        formatPhone(number: string, withCountryCode: boolean = false): string {
            return formatPhone(number, withCountryCode);
        },

        getLeadType(id: string | number): string {
            return getLeadTypeById(id);
        },

        getLeadTypeIcon(lead: any): string {
            const leadTypeId = get(lead, 'lead_type_id');

            // Keep previous behavior: unknown/missing lead type returns empty string.
            if (!getLeadTypeById(leadTypeId)) {
                return '';
            }

            return LeadType.getIcon(leadTypeId) ?? getIcons().leadXpress ?? '';
        },

        formatExtension(text: string): string {
            if (!text) return '';

            text = text.replace(/\D/g, '');

            if (!text) return '';

            return text;
        },

        as_locale(
            date: any,
            key: string,
            format: string = '',
            timezone: string = '',
            defaultNow: boolean = false
        ): any {
            return Moment.asLocale(date, key, { format, timezone, defaultNow });
        },

        locale_dt(
            date: any,
            format: string = '',
            timezone: string = '',
            defaultNow: boolean = false
        ): any {
            return Moment.init(date, 'dateTime', {
                format,
                timezone,
                defaultNow,
            });
        },

        localeDate(
            date: any,
            format: string = '',
            timezone: string = '',
            defaultNow: boolean = false
        ): any {
            return Moment.init(date, 'date', { format, timezone, defaultNow });
        },

        locale_time(time: any, timezone: string): any {
            return Moment.init(time, 'time', { timezone });
        },

        empty(value: any): boolean {
            return isEmpty(value);
        },

        updateQuery(newQueryData: Record<string, any>): void {
            const oldQuery = route?.query ?? {};
            const newQuery = {
                ...oldQuery,
                ...newQueryData,
            };

            const parsedNewQuery = parseNewQuery({
                oldQuery,
                newQuery,
            });

            if (parsedNewQuery === null) {
                // No changes to query
                return;
            }

            router?.replace?.({ query: parsedNewQuery });
        },

        getFileName(fileString: string): string {
            const firstIndex = fileString.lastIndexOf('/') + 1;
            const lastIndex = fileString.length;

            return fileString.slice(firstIndex, lastIndex);
        },

        includeInLayout(item: string): boolean {
            return get(route?.meta, `layout.${item}`, true);
        },
    };
};

const safeUseRoute = (): RouteLike | undefined => {
    try {
        return useRoute() as unknown as RouteLike;
    } catch {
        return undefined;
    }
};

const safeUseRouter = (): RouterLike | undefined => {
    try {
        return useRouter() as unknown as RouterLike;
    } catch {
        return undefined;
    }
};

export const useGlobal = (
    options: UseGlobalOptions = {}
): { mounted: Ref<boolean> } & GlobalMethods => {
    const instance = getCurrentInstance();
    const route = options.route ?? safeUseRoute();
    const router = options.router ?? safeUseRouter();
    const mounted = ref(false);

    const resolveIcons = (): IconsMap => {
        if (options.icons) {
            return isRef(options.icons) ? options.icons.value : options.icons;
        }

        return ((instance?.appContext.config.globalProperties as Record<string, any>)?.$icons ??
            {}) as IconsMap;
    };

    const translate =
        options.translate ??
        ((key: string) => {
            const t = (instance?.proxy as { $t?: (input: string) => string } | null)?.$t;

            if (typeof t === 'function') {
                return t(key);
            }

            return defaultTranslate(key);
        });

    return {
        mounted,
        ...createGlobalMethods({
            route,
            router,
            translate,
            getIcons: resolveIcons,
            resetData: options.resetData,
        }),
    };
};

export default useGlobal;
